/* hs-eslint ignored failing-rules */

/* eslint-disable react/prop-types */
'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { Fragment, PureComponent, useCallback } from 'react';
import { Map as ImmutableMap, List } from 'immutable';
import Networks from 'ads-account-connection/lib/Networks';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIButton from 'UIComponents/button/UIButton';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UILink from 'UIComponents/link/UILink';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import MissingAppPermissionsWarning from './MissingAppPermissionsWarning';
import NoAdAccountsConnectModal from 'ads-account-connection/components/ad-accounts/NoAdAccountsConnectModal';
import H2 from 'UIComponents/elements/headings/H2';
import AdAccountsModalTable from 'ads-account-connection/components/ad-accounts/AdAccountsModalTable';
import KnowledgeBaseLink from 'ads-account-connection/components/KnowledgeBaseLink';
import { KBArticles } from 'ads-account-connection/lib/Constants';
import { AD_CREDIT_URLS, LEARN_MORE_URLS } from 'ads-account-connection/lib/AdAccountHelpUrls';

const AdAccountConnectDialogByType = ({
  network,
  trackEvent,
  isDeveloperPortal
}) => {
  switch (network) {
    case Networks.ADWORDS:
      return /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsxs("p", {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "ADS_ACCOUNT_CONNECTION.adAccountConnectModal.adwordsMcc"
          }), ' ', /*#__PURE__*/_jsx(KnowledgeBaseLink, {
            kbArticle: KBArticles.CONNECT_ADWORDS_ADS_ACCOUNT,
            context: "ad-account-connect-adwords",
            trackEvent: trackEvent
          })]
        }), !isDeveloperPortal && /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "ADS_ACCOUNT_CONNECTION.adAccountConnectModal.coupon_jsx",
            options: {
              href: AD_CREDIT_URLS[Networks.ADWORDS],
              external: true
            },
            elements: {
              Link: UILink
            }
          })
        })]
      });

    case Networks.FACEBOOK:
      return /*#__PURE__*/_jsxs("p", {
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "ADS_ACCOUNT_CONNECTION.adAccountConnectModal.connectAccount"
        }), ' ', /*#__PURE__*/_jsx(KnowledgeBaseLink, {
          kbArticle: KBArticles.CONNECT_FACEBOOK_ADS_ACCOUNT,
          context: "ad-account-connect-facebook",
          trackEvent: trackEvent
        })]
      });

    case Networks.LINKEDIN:
      return /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsxs("p", {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "ADS_ACCOUNT_CONNECTION.adAccountConnectModal.connectAccount"
          }), ' ', /*#__PURE__*/_jsx(KnowledgeBaseLink, {
            kbArticle: KBArticles.CONNECT_LINKEDIN_ADS_ACCOUNT,
            context: "ad-account-connect-linkedin",
            trackEvent: trackEvent
          })]
        }), /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "ADS_ACCOUNT_CONNECTION.adAccountConnectModal.coupon_jsx",
          options: {
            href: AD_CREDIT_URLS[Networks.LINKEDIN],
            external: true
          },
          elements: {
            Link: UILink
          }
        })]
      });

    default:
      {
        return null;
      }
  }
};

const NewAdAccountButton = ({
  network,
  onClickCreateAdAccount
}) => {
  const handleClick = useCallback(() => {
    onClickCreateAdAccount(network);
  }, [network, onClickCreateAdAccount]);
  return /*#__PURE__*/_jsx(UIButton, {
    use: "link",
    external: true,
    href: LEARN_MORE_URLS[network],
    onClick: handleClick,
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "ADS_ACCOUNT_CONNECTION.adAccountConnectModal.newAdAccount"
    })
  });
};

class AdAccountConnectModal extends PureComponent {
  constructor(props) {
    super(props);
    this.handleSelectionConfirmed = this.handleSelectionConfirmed.bind(this);
    this.setSelectedAdAccounts = this.setSelectedAdAccounts.bind(this);
    this.state = {
      selectedAdAccounts: ImmutableMap()
    };
  }

  componentDidMount() {
    const {
      availableAccounts
    } = this.props;
    const selectedAdAccounts = availableAccounts.filter(account => account.currentUserHasConnected).map(account => [account.adAccountId, account.trackingEnabled]).toArray();
    this.setState({
      selectedAdAccounts: ImmutableMap(selectedAdAccounts)
    });
  }

  handleSelectionConfirmed() {
    const {
      selectedAdAccounts
    } = this.state;
    this.props.onSelect(selectedAdAccounts);
  }

  handleAdAccountTrackingToggled(id) {
    return () => {
      const {
        selectedAdAccounts
      } = this.state;
      this.setState({
        selectedAdAccounts: selectedAdAccounts.set(id, !selectedAdAccounts.get(id))
      });
    };
  }

  setSelectedAdAccounts(selectedAdAccounts) {
    this.setState({
      selectedAdAccounts
    });
  }

  isPrimaryButtonDisabled() {
    const {
      selectedAdAccounts
    } = this.state;
    const {
      maxSelectableAccounts,
      availableAccounts,
      allowDeselect
    } = this.props;
    const selectedUniqueAccounts = selectedAdAccounts.filter((__trackingEnabled, accountId) => {
      const availableAccount = availableAccounts.find(account => accountId === account.adAccountId);
      return !availableAccount.currentUserHasConnected;
    });
    return (allowDeselect ? selectedAdAccounts.size === 0 : selectedUniqueAccounts.size === 0) || selectedUniqueAccounts.size > maxSelectableAccounts;
  }

  render() {
    const {
      loading,
      maxSelectableAccounts,
      network,
      onReauth,
      showAdAccountLimits,
      showMissingPermissionsWarning,
      availableAccounts,
      onClickCreateAdAccount,
      allowDeselect,
      trackEvent,
      onShowCreationModal
    } = this.props;
    const {
      selectedAdAccounts
    } = this.state;
    const selectedNewAccounts = selectedAdAccounts.filter((__trackingEnabled, accountId) => {
      const availableAccount = availableAccounts.find(account => accountId === account.adAccountId);
      return !availableAccount.currentUserHasConnected;
    });

    if (availableAccounts.isEmpty()) {
      return /*#__PURE__*/_jsx(NoAdAccountsConnectModal, Object.assign({}, this.props));
    }

    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
        children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
          onClick: this.props.onCancel
        }), /*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `ADS_ACCOUNT_CONNECTION.adAccountConnectModal.title.${network}`,
            options: {
              count: maxSelectableAccounts
            }
          })
        })]
      }), /*#__PURE__*/_jsxs(UIDialogBody, {
        children: [/*#__PURE__*/_jsx(AdAccountConnectDialogByType, {
          network: network,
          trackEvent: trackEvent,
          isDeveloperPortal: this.props.isDeveloperPortal
        }), showAdAccountLimits && /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "ADS_ACCOUNT_CONNECTION.adAccountConnectModal.limits.header",
            options: {
              count: maxSelectableAccounts
            }
          })
        }), showMissingPermissionsWarning && /*#__PURE__*/_jsx(MissingAppPermissionsWarning, {
          network: network,
          onReauth: onReauth,
          trackEvent: trackEvent
        }), /*#__PURE__*/_jsx(AdAccountsModalTable, {
          availableAccounts: availableAccounts,
          maxSelectableAccounts: maxSelectableAccounts,
          selectedAdAccounts: selectedAdAccounts,
          selectedNewAccounts: selectedNewAccounts,
          setSelectedAdAccounts: this.setSelectedAdAccounts,
          allowDeselect: allowDeselect,
          trackEvent: trackEvent
        })]
      }), /*#__PURE__*/_jsxs(UIDialogFooter, {
        children: [/*#__PURE__*/_jsx(UILoadingButton, {
          use: "primary",
          className: "confirm-button",
          disabled: this.isPrimaryButtonDisabled(),
          loading: loading,
          onClick: this.handleSelectionConfirmed,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ADS_ACCOUNT_CONNECTION.adAccountConnectModal.connectButton"
          })
        }), /*#__PURE__*/_jsx(UIButton, {
          onClick: this.props.onCancel,
          className: "cancel-button",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ADS_ACCOUNT_CONNECTION.adAccountConnectModal.cancelButton"
          })
        }), network === Networks.ADWORDS ? /*#__PURE__*/_jsx(UIButton, {
          use: "link",
          onClick: onShowCreationModal,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ADS_ACCOUNT_CONNECTION.adAccountConnectModal.newAdAccount"
          })
        }) : /*#__PURE__*/_jsx(NewAdAccountButton, {
          network: network,
          onClickCreateAdAccount: onClickCreateAdAccount
        })]
      })]
    });
  }

}

AdAccountConnectModal.propTypes = {
  network: PropTypes.oneOf(Object.keys(Networks)).isRequired,
  availableAccounts: PropTypes.instanceOf(List).isRequired,
  onCancel: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  maxSelectableAccounts: PropTypes.number.isRequired,
  showAdAccountLimits: PropTypes.bool.isRequired,
  showMissingPermissionsWarning: PropTypes.bool,
  onClickCreateAdAccount: PropTypes.func.isRequired,
  trackEvent: PropTypes.func,
  isDeveloperPortal: PropTypes.bool
};
export default AdAccountConnectModal;